import { useQueries } from '@tanstack/react-query';
import {
	CSSProperties,
	memo,
	Suspense,
	useCallback,
	useEffect,
	useState,
} from 'react';
import 'swiper/css';
import { A11y, Keyboard } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper/types';

import { MatchDayCentreTakeover as MatchDayCentreTakeoverData } from 'services/freeform';
import { GameOverview, getGameOverview } from 'services/matchday';

import CardMdc from 'core/generic/components/cards/card-mdc';
import CarouselIndicator from 'core/generic/components/general/carousel-indicator';
import LoadingSkeleton from 'core/generic/components/general/loading-skeleton';
import Countdown from 'core/generic/elements/countdown';
import { Icon } from 'core/generic/elements/icons';

import {
	mapGameOverviewToCardMdc,
	resolveMatchBackgroundUrl,
} from '@home/utils/map-game-overview-to-card-mdc';

import styles from './match-day-takeover.module.scss';

type MatchDayTakeoverProps = {
	data: MatchDayCentreTakeoverData;
};

const SWIPER_MODULES = [A11y, Keyboard];
const MOBILE_SLIDE_OFFSET = 16;

const SWIPER_BREAKPOINTS = {
	600: {
		slidesPerView: 1,
		slidesOffsetBefore: 0,
		slidesOffsetAfter: 0,
		spaceBetween: 24,
	},
	1136: {
		slidesPerView: 1,
		slidesOffsetBefore: 0,
		slidesOffsetAfter: 0,
		spaceBetween: 32,
	},
};

type MatchCardProps = {
	overview: GameOverview | null | undefined;
	pageUrl?: string;
	teamLabel?: string;
	kickOffTimeZone?: string;
	isLoading: boolean;
	noRadius?: boolean;
	/** Client-only: keeps SSR/first paint as an empty shell (hydration-safe). */
	showSkeletonLoader?: boolean;
};

// Memoised so carousel interaction (activeIndex state) and background
// refetches with unchanged data (react-query structural sharing keeps
// `overview` referentially stable) don't re-render every card.
const MatchCard = memo(function MatchCard({
	overview,
	pageUrl,
	teamLabel,
	kickOffTimeZone,
	isLoading,
	noRadius = false,
	showSkeletonLoader = false,
}: MatchCardProps) {
	if (isLoading || !overview) {
		return (
			<div
				className={styles.skeleton}
				aria-busy="true"
				data-testid="match-card-skeleton"
			>
				{showSkeletonLoader ? (
					<Suspense fallback={null}>
						<LoadingSkeleton
							showHeading={false}
							bodyLines="four-line"
							className={styles.skeletonLoader}
						/>
					</Suspense>
				) : null}
			</div>
		);
	}

	const card = mapGameOverviewToCardMdc(overview, {
		pageUrl,
		teamLabel,
		kickOffTimeZone,
	});

	const countdown =
		card.state === 'pre-match' && card.kickOffUTC ? (
			<Suspense fallback={null}>
				<Countdown
					targetDate={card.kickOffUTC}
					size="medium"
					variant="primary"
				/>
			</Suspense>
		) : undefined;

	return (
		<div className={styles.slide}>
			<Suspense fallback={null}>
				<CardMdc
					className={styles.card}
					state={card.state}
					teamLabel={card.teamLabel}
					competitionName={card.competitionName}
					competitionCrestUrl={card.competitionCrestUrl}
					competitionBackgroundUrl={card.competitionBackgroundUrl}
					competitionStatus={card.competitionStatus}
					competitionStatusAccent={card.competitionStatusAccent}
					homeTeam={card.homeTeam}
					awayTeam={card.awayTeam}
					homeScore={card.homeScore}
					awayScore={card.awayScore}
					kickOffLabel={card.kickOffLabel}
					matchMinuteLabel={card.matchMinuteLabel}
					halfTimeLabel={card.halfTimeLabel}
					fullTimeLabel={card.fullTimeLabel}
					countdown={countdown}
					image={card.image}
					video={card.video}
					ctaHref={card.ctaHref}
					ctaLabel={card.ctaLabel}
					showStatusIndicator={card.showStatusIndicator}
					noRadius={noRadius}
				/>
			</Suspense>
		</div>
	);
});

export const MatchDayTakeover = ({ data }: MatchDayTakeoverProps) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const [swiper, setSwiper] = useState<SwiperType | null>(null);
	// Gate fetches until after mount so SSR HTML and the client's first paint
	// both render skeletons (React Query otherwise diverges: idle on server,
	// fetching on client).
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
	}, []);

	const handleSlideChange = useCallback((instance: SwiperType) => {
		setActiveIndex(instance.activeIndex);
	}, []);

	const handleDotClick = useCallback(
		(index: number) => swiper?.slideTo(index),
		[swiper]
	);
	const handlePreviousClick = useCallback(
		() => swiper?.slidePrev(),
		[swiper]
	);
	const handleNextClick = useCallback(() => swiper?.slideNext(), [swiper]);

	const centres =
		data.matchDayCentres && data.matchDayCentres.length > 0
			? data.matchDayCentres
			: data.refreshUrl
				? [
						{
							team: 'default',
							refreshUrl: data.refreshUrl,
							pageUrl: data.pageUrl || '#',
							kickOffTimeZone: data.kickOffTimeZone,
						},
					]
				: [];

	const queries = useQueries({
		queries: centres.map((centre) => ({
			queryKey: ['game-overview', centre.refreshUrl],
			queryFn: () => getGameOverview(centre.refreshUrl),
			staleTime: 60_000,
			refetchInterval: 60_000,
			enabled: isClient && Boolean(centre.refreshUrl),
		})),
	});

	if (centres.length === 0) {
		return null;
	}

	const hasAnyData = queries.some((query) => Boolean(query.data));
	const isSettledEmpty =
		isClient &&
		!hasAnyData &&
		queries.every((query) => !query.isLoading && !query.isFetching);

	if (isSettledEmpty) {
		return null;
	}

	const backgroundUrl = resolveMatchBackgroundUrl(
		queries[activeIndex]?.data ??
			queries.find((query) => query.data)?.data ??
			undefined
	);
	const isSingleRecord = centres.length === 1;

	const slides = centres.map((centre, index) => {
		const query = queries[index];
		const overview = query?.data;
		const isLoading = !isClient || !overview || Boolean(query?.isLoading);

		return {
			key: centre.team || centre.refreshUrl,
			content: (
				<MatchCard
					overview={overview}
					pageUrl={centre.pageUrl}
					teamLabel={centre.team}
					kickOffTimeZone={
						centre.kickOffTimeZone || data.kickOffTimeZone
					}
					isLoading={isLoading}
					noRadius={isSingleRecord}
					showSkeletonLoader={isClient}
				/>
			),
		};
	});
	const hasMultipleSlides = slides.length > 1;

	return (
		<section
			className={[
				styles.root,
				!hasMultipleSlides ? styles.rootSingle : '',
			]
				.filter(Boolean)
				.join(' ')}
			aria-label="Match day centre"
			style={
				backgroundUrl
					? ({
							['--mdc-background-image']: `url("${backgroundUrl}")`,
						} as CSSProperties)
					: undefined
			}
		>
			<div className={styles.background} aria-hidden="true">
				<div className={styles.backdrop} />
			</div>
			<div
				className={[
					styles.inner,
					!hasMultipleSlides ? styles.innerSingle : '',
				]
					.filter(Boolean)
					.join(' ')}
			>
				<div
					className={[
						styles.carousel,
						!hasMultipleSlides ? styles.carouselSingle : '',
					]
						.filter(Boolean)
						.join(' ')}
				>
					{hasMultipleSlides ? (
						<nav
							className={styles.navigation}
							aria-label="Match day centre slides"
						>
							<button
								className={styles.navigationButton}
								type="button"
								aria-label="Previous match day centre"
								disabled={activeIndex === 0}
								onClick={handlePreviousClick}
							>
								<Icon variant="ChevronLeft" />
							</button>
							<button
								className={styles.navigationButton}
								type="button"
								aria-label="Next match day centre"
								disabled={activeIndex === slides.length - 1}
								onClick={handleNextClick}
							>
								<Icon variant="ChevronRight" />
							</button>
						</nav>
					) : null}
					{hasMultipleSlides ? (
						<Swiper
							className={styles.swiper}
							modules={SWIPER_MODULES}
							slidesPerView={1}
							slidesOffsetBefore={MOBILE_SLIDE_OFFSET}
							slidesOffsetAfter={MOBILE_SLIDE_OFFSET}
							spaceBetween={16}
							keyboard
							a11y
							breakpoints={SWIPER_BREAKPOINTS}
							onSlideChange={handleSlideChange}
							onSwiper={setSwiper}
						>
							{slides.map((slide) => (
								<SwiperSlide
									key={slide.key}
									className={styles.swiperSlide}
								>
									{slide.content}
								</SwiperSlide>
							))}
						</Swiper>
					) : (
						slides[0]?.content
					)}

					{hasMultipleSlides ? (
						<div className={styles.indicators}>
							<Suspense fallback={null}>
								<CarouselIndicator
									total={slides.length}
									activeIndex={activeIndex}
									onDotClick={handleDotClick}
								/>
							</Suspense>
						</div>
					) : null}
				</div>
			</div>
		</section>
	);
};

export default MatchDayTakeover;
